import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Info } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { useAuth } from '@/hooks/useAuth'
import { toast } from '@/components/ui/sonner'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

const DangerZoneSettings = () => {
  const { closeAccount } = useAuth()
  const [isClosing, setIsClosing] = useState(false)
  const navigate = useNavigate()

  const handleCloseAccount = async () => {
    setIsClosing(true)
    try {
      await closeAccount()
      toast.success('Your account has been closed.')
      navigate('/signin', { replace: true })
    } catch (err: any) {
      toast.error(err.message || 'Failed to close account')
      setIsClosing(false)
    }
  }

  return (
    <Card variant="dark" className="border-red-500/20">
      <CardHeader>
        <CardTitle className="text-white">Danger Zone</CardTitle>
        <CardDescription className="text-gray-400">Permanently close your account</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="mb-4 flex items-center gap-2 text-sm text-gray-300">
          <p>Closing your account signs you out and removes you from marketplace search.</p>
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" aria-label="Learn more about account closure">
                <Info className="h-4 w-4 text-gray-400" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              Your account is deactivated immediately. After the applicable retention period, we
              delete personal data we no longer need and de-identify or isolate information we must
              retain for legal, tax, security, or recordkeeping purposes.
            </TooltipContent>
          </Tooltip>
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" disabled={isClosing}>
              Close my account
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Close your account?</AlertDialogTitle>
              <AlertDialogDescription>
                This will immediately close your account and sign you out everywhere. This cannot
                be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleCloseAccount}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Close my account
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  )
}

export default DangerZoneSettings
